import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { Wrap, H2, P } from "@/components/Content";
import { Seo, eventSchema, breadcrumbSchema } from "@/components/Seo";
import {
  ArrowRight, Moon, Users, Mic, ShieldCheck, Ban, Check, MapPin, CalendarClock,
} from "lucide-react";

const AH_IMG =
  "https://static.prod-images.emergentagent.com/jobs/c7daeff9-dc71-419e-8f04-11b938fd666e/images/936391d26b32f7bef1edc162033299336450d004aaa115a8569557f64cd5f58c.jpeg";

const INPUT =
  "w-full rounded-xl bg-[#0d0d10] border border-[color:var(--line)] px-4 py-3 text-sm text-white placeholder:text-[color:var(--chrome-3)] focus:outline-none focus:border-[color:var(--cyan)] transition-colors";

const TOPICS = [
  "Pokémon collecting", "Sports cards", "Baseball cards", "Football cards", "Basketball cards",
  "Vintage cards", "Modern cards", "Comic books", "Set building", "Rare finds",
  "Grading experiences", "Card condition", "Preservation", "Storage", "Display",
  "Collecting on a budget", "High-value collections", "Sentimental collections", "Lessons learned",
  "Mistakes to avoid", "Favorite cards or comics", "Hobby history", "Buying responsibly",
  "Protecting collections", "Inherited collections", "Collector stories", "The changing hobby",
  "What makes a collection meaningful",
];

const RELATED = [
  { label: "About The Lab", to: "/about" },
  { label: "Trust Center", to: "/trust-center" },
  { label: "Card Services", to: "/card-services" },
  { label: "Comic Services", to: "/comic-services" },
  { label: "Submit A Collectible", to: "/submit" },
  { label: "Contact", to: "/contact" },
];

const Label = ({ children, required }) => (
  <label className="block text-xs font-tech tracking-wide text-[color:var(--chrome-2)] mb-1.5">
    {children} {required && <span className="text-[color:var(--magenta)]">*</span>}
  </label>
);

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 90, behavior: "smooth" });
};

export default function AfterHours() {
  return (
    <div data-testid="after-hours-page" className="bg-ink">
      <Seo
        title="CardScience After Hours — Collector Community | CardScience HQ"
        description="An evening discussion series for collectors. Real conversations, real experience, no sales pitch. Apply to guest host or join the list for the next CardScience After Hours session."
        path="/after-hours"
        jsonLd={[
          eventSchema({ name: "CardScience After Hours", description: "An evening collector discussion series — collectors helping collectors, no sales pitch.", path: "/after-hours" }),
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "After Hours", path: "/after-hours" }]),
        ]}
      />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={AH_IMG} alt="Collectors in conversation at CardScience After Hours" className="h-full w-full object-cover object-center opacity-60" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(6,6,8,0.55) 0%, rgba(6,6,8,0.4) 40%, rgba(6,6,8,0.85) 82%, #060608 100%)" }} />
        </div>
        <div className="relative mx-auto max-w-[1100px] px-5 py-20 sm:py-28">
          <p className="eyebrow mb-5 flex items-center gap-2"><Moon size={14} className="text-[color:var(--violet)]" /> Collector Community</p>
          <h1 className="h-display text-4xl sm:text-5xl lg:text-6xl">
            <span className="text-chrome">CARDSCIENCE </span><span className="text-holo">AFTER HOURS</span>
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-[color:var(--chrome-1)] leading-relaxed">
            Collector conversations.<br />Real experience.<br />
            <span className="text-holo font-semibold">No sales pitch.</span>
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-9 max-w-lg">
            <button data-testid="apply-guest-host-cta" onClick={() => scrollTo("guest-host")}
              className="btn-holo rounded-xl px-7 py-4 text-sm inline-flex items-center justify-center gap-2 flex-1">
              APPLY TO GUEST HOST <ArrowRight size={16} />
            </button>
            <button data-testid="join-list-cta" onClick={() => scrollTo("attendee")}
              className="btn-ghost rounded-xl px-7 py-4 text-sm flex-1">
              JOIN THE AFTER HOURS LIST
            </button>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <Wrap className="py-16">
        <P>CardScience HQ is creating an evening discussion series for collectors who want to go deeper into the hobby.</P>
        <P>The goal is simple: <span className="text-white font-semibold">collectors helping collectors.</span></P>
        <P>We want real conversations about collecting, preserving, grading, buying, learning, mistakes, discoveries, favorite pieces and the experiences that make this hobby worth being part of.</P>
        <P>This is not about selling from a stage. It is about sharing knowledge.</P>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
          {[
            { icon: Users, t: "Collectors, not vendors", d: "An intimate room of people who genuinely love the hobby." },
            { icon: Mic, t: "Real conversations", d: "Honest talk about wins, mistakes and lessons learned." },
            { icon: CalendarClock, t: "Evening sessions", d: "After-hours discussions hosted inside the lab." },
          ].map((c) => (
            <div key={c.t} className="rounded-2xl glass p-5">
              <c.icon size={22} className="text-[color:var(--cyan)]" />
              <p className="text-white font-semibold mt-3">{c.t}</p>
              <p className="text-sm text-[color:var(--chrome-2)] mt-1 leading-relaxed">{c.d}</p>
            </div>
          ))}
        </div>
      </Wrap>

      {/* LOOKING FOR COLLECTORS */}
      <div className="border-t border-[color:var(--line)] bg-[#0b0b0e]">
        <Wrap className="py-16">
          <p className="eyebrow mb-3">Who We're Looking For</p>
          <H2>WE'RE LOOKING FOR COLLECTORS. <span className="text-holo">NOT DEALERS.</span></H2>
          <div className="mt-6 space-y-2.5">
            {[
              "You do not need to own a card shop.",
              "You do not need to sell cards.",
              "You do not need to be an influencer.",
              "You do not need a huge social following.",
            ].map((t) => (
              <div key={t} className="flex items-start gap-3">
                <Ban size={16} className="text-[color:var(--magenta)] shrink-0 mt-0.5" />
                <span className="text-sm text-[color:var(--chrome-1)]">{t}</span>
              </div>
            ))}
          </div>
          <P className="mt-6">You simply need to be a collector with real experience, a real point of view and something worth sharing.</P>
          <P>We are looking for people who love the hobby and can speak honestly about what they have learned.</P>
        </Wrap>
      </div>

      {/* TOPICS */}
      <Wrap className="py-16">
        <p className="eyebrow mb-3">Guest Host Topics</p>
        <H2>WHAT WE COULD TALK ABOUT</H2>
        <div className="flex flex-wrap gap-2 mt-6">
          {TOPICS.map((t) => (
            <span key={t} className="rounded-full border border-[color:var(--line)] bg-[#0d0d10] px-3.5 py-2 text-xs text-[color:var(--chrome-1)] hover:border-[color:var(--cyan)] transition-colors">
              {t}
            </span>
          ))}
        </div>
      </Wrap>

      {/* CULTURE RULE */}
      <div className="border-y border-[color:var(--line)]">
        <Wrap className="py-14 text-center">
          <ShieldCheck size={26} className="text-[color:var(--cyan)] mx-auto mb-4" />
          <h3 className="h-display text-2xl sm:text-3xl text-chrome">
            NO DEALER PITCHES. <span className="text-holo">NO AUCTION PLUGS.</span> NO SELLING FROM THE STAGE.
          </h3>
          <p className="text-sm text-[color:var(--chrome-2)] mt-4 max-w-2xl mx-auto leading-relaxed">
            This is about collectors helping collectors. CardScience After Hours is meant to feel authentic, useful and community-driven.
          </p>
        </Wrap>
      </div>

      {/* GUEST HOST FORM */}
      <div id="guest-host" className="bg-[#0b0b0e] border-b border-[color:var(--line)]">
        <Wrap className="py-16">
          <p className="eyebrow mb-3">Guest Host</p>
          <H2>HAVE SOMETHING WORTH SHARING?</H2>
          <P className="mt-4">If you are a collector with experience, knowledge or a story that other collectors would benefit from hearing, we want to hear from you.</P>
          <HostForm />
          <p className="text-xs text-[color:var(--chrome-3)] mt-4 leading-relaxed">
            You do not need to be famous. You need to know your subject and bring value to collectors.
          </p>
        </Wrap>
      </div>

      {/* ATTENDEE FORM */}
      <div id="attendee">
        <Wrap className="py-16">
          <p className="eyebrow mb-3">Attend A Session</p>
          <H2>WANT A SEAT AT THE NEXT SESSION?</H2>
          <P className="mt-4">Join the CardScience After Hours list and we will let you know when the next collector discussion opens.</P>
          <SignupForm />
        </Wrap>
      </div>

      {/* PRIVACY */}
      <div className="border-t border-[color:var(--line)] bg-[#0b0b0e]">
        <Wrap className="py-14">
          <p className="eyebrow mb-3 flex items-center gap-2"><ShieldCheck size={14} className="text-[color:var(--cyan)]" /> Privacy</p>
          <P>All guest host and attendee information is private by default.</P>
          <P>We do not publicly post application details, attendee names, collector identities or collection information without explicit permission.</P>
          <P>Any photos, video clips, names, bios or recordings used for marketing require separate permission.</P>
        </Wrap>
      </div>

      {/* FINAL BRAND LINE */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-60" style={{ background: "radial-gradient(circle at 50% 120%, rgba(155,92,255,0.35), transparent 60%)" }} />
        <Wrap className="py-20 text-center relative">
          <p className="font-tech text-[11px] tracking-[0.4em] text-[color:var(--chrome-3)]">CARDSCIENCE AFTER HOURS</p>
          <h2 className="h-display text-3xl sm:text-5xl mt-4">
            <span className="text-chrome">FOR COLLECTORS.</span><br />
            <span className="text-holo">BY COLLECTORS.</span><br />
            <span className="text-chrome">NO SALES PITCH.</span>
          </h2>
          <div className="flex flex-wrap items-center justify-center gap-2 mt-10">
            {RELATED.map((r) => (
              <Link key={r.to} to={r.to} className="rounded-full border border-[color:var(--line)] px-4 py-2 text-xs font-tech tracking-wide text-[color:var(--chrome-2)] hover:text-[color:var(--cyan)] hover:border-[color:var(--cyan)] transition-colors">
                {r.label}
              </Link>
            ))}
          </div>
        </Wrap>
      </div>
    </div>
  );
}

function HostForm() {
  const empty = {
    full_name: "", email: "", phone: "", location: "", collects: "", collecting_since: "",
    topic: "", why_valuable: "", format: "either", social_link: "", bio: "", anything_else: "",
  };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.full_name.trim() || !f.email.trim()) return toast.error("Name and email are required.");
    setBusy(true);
    try {
      await axios.post(`${API}/after-hours/host-application`, f);
      setDone(true);
      toast.success("Application received — thank you.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid="ah-host-success" className="mt-8 rounded-2xl holo-border holo-glow p-8 text-center">
        <Check size={30} className="text-[color:var(--cyan)] mx-auto mb-3" />
        <p className="h-display text-xl text-chrome">THANK YOU FOR APPLYING</p>
        <p className="text-sm text-[color:var(--chrome-2)] mt-2 max-w-md mx-auto">
          We've received your guest host application. If it's a fit for an upcoming session, we'll reach out privately.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mt-8 rounded-2xl glass p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
      <div><Label required>Full name</Label><input data-testid="ah-host-name" className={INPUT} value={f.full_name} onChange={set("full_name")} /></div>
      <div><Label required>Email</Label><input data-testid="ah-host-email" type="email" className={INPUT} value={f.email} onChange={set("email")} /></div>
      <div><Label>Phone</Label><input data-testid="ah-host-phone" className={INPUT} value={f.phone} onChange={set("phone")} /></div>
      <div><Label>City / State</Label><input data-testid="ah-host-location" className={INPUT} value={f.location} onChange={set("location")} /></div>
      <div><Label>What do you collect?</Label><input data-testid="ah-host-collects" className={INPUT} value={f.collects} onChange={set("collects")} /></div>
      <div><Label>How long have you been collecting?</Label><input data-testid="ah-host-since" className={INPUT} value={f.collecting_since} onChange={set("collecting_since")} /></div>
      <div className="sm:col-span-2"><Label>What topic would you like to discuss?</Label><input data-testid="ah-host-topic" className={INPUT} value={f.topic} onChange={set("topic")} /></div>
      <div className="sm:col-span-2"><Label>Why would collectors benefit from your discussion?</Label><textarea data-testid="ah-host-why" rows={3} className={INPUT} value={f.why_valuable} onChange={set("why_valuable")} /></div>
      <div>
        <Label>In-person or virtual</Label>
        <select data-testid="ah-host-format" className={INPUT} value={f.format} onChange={set("format")}>
          <option value="either">Either works</option>
          <option value="in_person">In-person</option>
          <option value="virtual">Virtual</option>
        </select>
      </div>
      <div><Label>Social link or website (optional)</Label><input data-testid="ah-host-social" className={INPUT} value={f.social_link} onChange={set("social_link")} /></div>
      <div className="sm:col-span-2"><Label>Short collector bio</Label><textarea data-testid="ah-host-bio" rows={3} className={INPUT} value={f.bio} onChange={set("bio")} /></div>
      <div className="sm:col-span-2"><Label>Anything else we should know?</Label><textarea data-testid="ah-host-else" rows={2} className={INPUT} value={f.anything_else} onChange={set("anything_else")} /></div>
      <div className="sm:col-span-2">
        <button data-testid="ah-host-submit" disabled={busy} className="btn-holo rounded-xl px-8 py-4 text-sm inline-flex items-center gap-2 disabled:opacity-50">
          {busy ? "SUBMITTING..." : "APPLY TO GUEST HOST"} <ArrowRight size={16} />
        </button>
      </div>
    </form>
  );
}

function SignupForm() {
  const empty = { full_name: "", email: "", phone: "", collects: "", location: "" };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.full_name.trim() || !f.email.trim()) return toast.error("Name and email are required.");
    setBusy(true);
    try {
      await axios.post(`${API}/after-hours/signup`, f);
      setDone(true);
      toast.success("You're on the list.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid="ah-signup-success" className="mt-8 rounded-2xl holo-border holo-glow p-8 text-center">
        <Check size={30} className="text-[color:var(--cyan)] mx-auto mb-3" />
        <p className="h-display text-xl text-chrome">YOU'RE ON THE LIST</p>
        <p className="text-sm text-[color:var(--chrome-2)] mt-2 max-w-md mx-auto">
          We'll let you know privately when the next CardScience After Hours session opens.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mt-8 rounded-2xl glass p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
      <div><Label required>Name</Label><input data-testid="ah-signup-name" className={INPUT} value={f.full_name} onChange={set("full_name")} /></div>
      <div><Label required>Email</Label><input data-testid="ah-signup-email" type="email" className={INPUT} value={f.email} onChange={set("email")} /></div>
      <div><Label>Phone (optional)</Label><input data-testid="ah-signup-phone" className={INPUT} value={f.phone} onChange={set("phone")} /></div>
      <div><Label>City / State</Label><input data-testid="ah-signup-location" className={INPUT} value={f.location} onChange={set("location")} /></div>
      <div className="sm:col-span-2"><Label>What do you collect?</Label><input data-testid="ah-signup-collects" className={INPUT} value={f.collects} onChange={set("collects")} /></div>
      <div className="sm:col-span-2">
        <button data-testid="ah-signup-submit" disabled={busy} className="btn-holo rounded-xl px-8 py-4 text-sm inline-flex items-center gap-2 disabled:opacity-50">
          {busy ? "JOINING..." : "JOIN THE AFTER HOURS LIST"} <ArrowRight size={16} />
        </button>
      </div>
    </form>
  );
}
